import { createSlice } from '@reduxjs/toolkit'
import {
  createTable,
  getAllCurrenciesList,
  getAllGameTablesThunk,
  getGameTableDetailsThunk,
  updateTable,
  deleteTable,
  getAllCashGameTablesThunk,
  getCashGameTableDetailsThunk,
  getCashGameHandsThunk,
  getCashGameHandHistoryThunk,
  getRakeConfigurationListThunk,
  getBuyInConfigurationListThunk,
  fetchActivityDashboardThunk,
  fetchTablePerformanceAnalytics
} from 'redux-thunk/thunk/gameTable.thunk'

const initialGameTableState = {
  allGameTables: null,
  tableDetails: null,
  isTableCreated: false,
  updatedTableDetails: null,
  currencies: null,
  allCashGameTables: null,
  cashGameTableDetails: null,
  cashGameHands: null,
  cashGameHandHistory: null,
  rakeConfigurations: { rows: [], count: 0 },
  buyInConfigurations: { rows: [], count: 0 },
  activityDashboard: { betsData: null, rakeData: null, roundsData: null },
  activityDashboardLoading: false,
  // Cash-games activity dashboard (performance analytics).
  performanceAnalytics: null,
  isLoading: false,
}

const gameTableSlice = createSlice({
  name: 'gameTable',
  initialState: initialGameTableState,
  extraReducers: (builder) => {
    builder
      .addCase(getAllGameTablesThunk.fulfilled, (state, action) => {
        return {
          ...state,
          allGameTables: action.payload
        }
      })
      .addCase(getAllGameTablesThunk.rejected, (state, action) => {
        return {
          ...state,
          allGameTables: { rows: [], count: 0 }
        }
      })
      .addCase(getGameTableDetailsThunk.fulfilled, (state, action) => {
        return {
          ...state,
          tableDetails: action.payload
        }
      })
      .addCase(getGameTableDetailsThunk.rejected, (state, action) => {
        return {
          ...state,
          tableDetails: null
        }
      })
      .addCase(createTable.fulfilled, (state, action) => {
        return {
          ...state,
          isTableCreated: action.payload
        }
      })
      .addCase(createTable.rejected, (state, action) => {
        return {
          ...state,
          isTableCreated: false
        }
      })
      .addCase(updateTable.fulfilled, (state, action) => {
        return {
          ...state,
          updatedTableDetails: action.payload
        }
      })
      .addCase(updateTable.rejected, (state, action) => {
        return {
          ...state,
          updatedTableDetails: null
        }
      })
      .addCase(deleteTable.fulfilled, (state, action) => {
        // After successful deletion, we don't need to update any specific state
        // The table list will be refreshed by the controller
        return state
      })
      .addCase(deleteTable.rejected, (state, action) => {
        return state
      })
      .addCase(getAllCurrenciesList.fulfilled, (state, action) => {
        return {
          ...state,
          currencies: action.payload
        }
      })
      .addCase(getAllCurrenciesList.rejected, (state, action) => {
        return {
          ...state,
          currencies: { rows: [], count: 0 }
        }
      })
      .addCase(getAllCashGameTablesThunk.fulfilled, (state, action) => {
        return {
          ...state,
          allCashGameTables: action.payload
        }
      })
      .addCase(getAllCashGameTablesThunk.rejected, (state) => {
        return {
          ...state,
          allCashGameTables: { rows: [], count: 0 }
        }
      })
      .addCase(getCashGameTableDetailsThunk.fulfilled, (state, action) => {
        return {
          ...state,
          cashGameTableDetails: action.payload
        }
      })
      .addCase(getCashGameTableDetailsThunk.rejected, (state) => {
        return {
          ...state,
          cashGameTableDetails: null
        }
      })
      .addCase(getCashGameHandsThunk.fulfilled, (state, action) => {
        return {
          ...state,
          cashGameHands: action.payload
        }
      })
      .addCase(getCashGameHandsThunk.rejected, (state) => {
        return {
          ...state,
          cashGameHands: null
        }
      })
      .addCase(getCashGameHandHistoryThunk.fulfilled, (state, action) => {
        return {
          ...state,
          cashGameHandHistory: action.payload
        }
      })
      .addCase(getCashGameHandHistoryThunk.rejected, (state) => {
        return {
          ...state,
          cashGameHandHistory: null
        }
      })
      .addCase(getRakeConfigurationListThunk.fulfilled, (state, action) => {
        return {
          ...state,
          rakeConfigurations: action.payload || { rows: [], count: 0 }
        }
      })
      .addCase(getRakeConfigurationListThunk.rejected, (state) => {
        return {
          ...state,
          rakeConfigurations: { rows: [], count: 0 }
        }
      })
      .addCase(getBuyInConfigurationListThunk.fulfilled, (state, action) => {
        return {
          ...state,
          buyInConfigurations: action.payload || { rows: [], count: 0 }
        }
      })
      .addCase(getBuyInConfigurationListThunk.rejected, (state) => {
        return {
          ...state,
          buyInConfigurations: { rows: [], count: 0 }
        }
      })

      .addCase(fetchActivityDashboardThunk.pending, (state) => {
        return {
          ...state,
          activityDashboardLoading: true
        }
      })

      .addCase(fetchActivityDashboardThunk.fulfilled, (state, action) => {
        return {
          ...state,
          activityDashboardLoading: false,
          activityDashboard: action.payload
        }
      })
      
      .addCase(fetchActivityDashboardThunk.rejected, (state) => {
        return {
          ...state,
          activityDashboardLoading: false,
          activityDashboard: { betsData: null, rakeData: null, roundsData: null }
        }
      })

      .addCase(fetchTablePerformanceAnalytics.pending, (state) => {
        return {
          ...state,
          isLoading: true
        }
      })
      .addCase(fetchTablePerformanceAnalytics.fulfilled, (state, action) => {
        return {
          ...state,
          isLoading: false,
          performanceAnalytics: action.payload
        }
      })
      .addCase(fetchTablePerformanceAnalytics.rejected, (state) => {
        return {
          ...state,
          isLoading: false,
          performanceAnalytics: null
        }
      })
  }
})

export default gameTableSlice.reducer
